import React, { useState } from 'react';
import {
  Badge,
  Button,
  CandidateProfileUtil,
  Card,
  CardHeader,
  CardSection,
  CondensedLarge,
  DateUtil,
  Flashy,
  Gutter,
  ImageCircle,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  SectionHeader,
  SmallHeader,
  Well,
} from '@axiom/ui';
import type { UseOnboardingTextReturn } from '@axiom/ui';
import type { Candidate, OpportunityOnboarding } from '@axiom/validation';
import { PossibleImageSizes } from '@axiom/const';

import { OnboardingUtil } from './onboarding-util';

export type OnboardingCandidateProps = {
  name: string;
  position: NonNullable<OpportunityOnboarding['positions']>[number];
  formatEngagementAddress: UseOnboardingTextReturn['formatEngagementAddress'];
  onSave: (options: {
    candidateId: Candidate['id'];
    callbackFn: () => void;
  }) => Promise<void>;
};

export const OnboardingCandidate = ({
  name,
  position,
  formatEngagementAddress,
  onSave,
}: OnboardingCandidateProps) => {
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const emailSentAt = OnboardingUtil.getEmailSentDate(position);
  const candidate = OnboardingUtil.getCandidate(position);

  const handleSendOnboardingEmailClick = async () => {
    if (!candidate || emailSent) {
      return;
    }
    await onSave({
      candidateId: candidate.id,
      callbackFn: () => {
        setEmailSent(true);
      },
    });
  };

  return (
    <Card name={name}>
      <CardHeader>
        <SmallHeader>Onboarding</SmallHeader>
      </CardHeader>
      <CardSection divider>
        <SectionHeader>
          Review each section in the template, and make any changes necessary.
        </SectionHeader>
        <Gutter bottom="8px" />
        <Paragraph>
          Once you are confident that all information is correct, you can send
          an onboarding email to the talent and they’ll see guidance in AFT.
        </Paragraph>
        <Gutter bottom="16px" />
        <Button
          name="SEND_ONBOARDING_EMAIL"
          disabled={emailSent}
          onClick={() => {
            void handleSendOnboardingEmailClick();
          }}
        >
          Send Onboarding Email
        </Button>
        {emailSentAt ? (
          <Gutter top="8px">
            <Flashy name="EMAIL_SENT_DATE" color="textSubtle">
              Last sent on {DateUtil.displayTimestamp(emailSentAt)}
            </Flashy>
          </Gutter>
        ) : null}
      </CardSection>
      <CardSection>
        <SectionHeader>Selected talent:</SectionHeader>
        <Gutter bottom="16px" />
        <Layout>
          <LayoutItem rightGutter="8px">
            <ImageCircle
              name="PROFILE_IMAGE"
              imageName={candidate?.calculatedDisplayName}
              src={
                candidate
                  ? CandidateProfileUtil.getProfileImageUri(
                      candidate,
                      PossibleImageSizes.W_100
                    )
                  : null
              }
              size="default"
            />
          </LayoutItem>
          <LayoutItem>
            <SectionHeader name="CANDIDATE_NAME">
              {candidate?.calculatedDisplayName}
            </SectionHeader>
            <CondensedLarge name="CANDIDATE_ADDRESS">
              {candidate
                ? OnboardingUtil.generateAddressDisplayName(candidate)
                : null}
            </CondensedLarge>
            <Badge name="STATUS" background="gray">
              {candidate?.employmentStatus}
            </Badge>
          </LayoutItem>
        </Layout>
        <Gutter bottom="16px" />
        <Well name="OPP_INFO" background="gray">
          <Gutter bottom="8px">
            <ParagraphHeader as="span">Engagement location:</ParagraphHeader>{' '}
            <Paragraph as="span" name="OPP_WORK_SITE">
              {position.worksite}
            </Paragraph>
          </Gutter>
          <Paragraph name="OPP_ADDRESS">
            {formatEngagementAddress().map(item => {
              return (
                <React.Fragment key={item}>
                  {item}
                  <br />
                </React.Fragment>
              );
            })}
          </Paragraph>
        </Well>
      </CardSection>
    </Card>
  );
};
